/**
 * Resize an image File client-side using a Canvas. Returns a new File
 * with at most `maxDim` on the longest side and JPEG quality `quality`.
 * Phone cameras routinely produce 5-10MB images; this keeps uploads fast
 * and well under our 3MB server limit.
 */
export async function compressImage(file, { maxDim = 1024, quality = 0.85, type = "image/jpeg" } = {}) {
  if (!file) return file;
  // Skip tiny files — already small enough.
  if (file.size < 350 * 1024) return file;
  // HEIC isn't decodable in Canvas — skip compression and let the server reject if too big.
  if (/heic|heif/i.test(file.type)) return file;

  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });

  const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise((res) => canvas.toBlob(res, type, quality));
  if (!blob) return file;
  return new File([blob], file.name.replace(/\.(\w+)$/i, ".jpg"), { type });
}

export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
